import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { HttpTypes } from "@medusajs/types"
import { queryKeys } from "@/lib/utils/query-keys"
import { sdk } from "@/lib/utils/sdk"
import { CHECKOUT_CART_FIELDS } from "@/lib/constants/cart-fields"
import { getStoredAttribution } from "@/lib/utils/attribution"
import { shouldRemoveStoredCartForError } from "@/lib/utils/cart-errors"
import {
  getStoredCart,
  removeStoredCart,
  setStoredCart,
  addItemOptimistically,
  createOptimisticCartItem,
  getCurrentCart,
  rollbackOptimisticCart,
  updateLineItemOptimistically,
  removeLineItemOptimistically,
  createOptimisticCart,
} from "@/lib/utils/cart"

/**
 * Build the metadata payload to merge into a brand-new cart. Today this
 * just stamps marketing-attribution params (utm_*, gclid, fbclid, …) on
 * the cart so back-office reporting can join orders to acquisition
 * channels without a separate analytics warehouse.
 */
const buildCartCreateMetadata = (): Record<string, unknown> | undefined => {
  const attribution = getStoredAttribution()
  if (!attribution) return undefined
  const filtered = Object.fromEntries(
    Object.entries(attribution).filter(([, value]) => Boolean(value))
  )
  if (Object.keys(filtered).length === 0) return undefined
  return { attribution: filtered }
}

const DEFAULT_CART_FIELDS = CHECKOUT_CART_FIELDS

export const useCart = ({ fields }: { fields?: string } = {}) => {
  return useQuery({
    queryKey: queryKeys.cart.current(fields),
    queryFn: async () => {
      const id = getStoredCart()
      if (!id) return null
      try {
        const { cart } = await sdk.store.cart.retrieve(id, {
          fields: fields || DEFAULT_CART_FIELDS,
        })
        return cart
      } catch (error) {
        if (shouldRemoveStoredCartForError(error)) {
          removeStoredCart()
          return null
        }
        throw error
      }
    },
    staleTime: 0
  })
}

export const useUpdateCart = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      fields = DEFAULT_CART_FIELDS,
      ...updates
    }: HttpTypes.StoreUpdateCart & { fields?: string }) => {
      const cartId = getStoredCart()
      if (!cartId) throw new Error("No cart found")
      const { cart } = await sdk.store.cart.update(cartId, updates, { fields })
      return cart
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ predicate: queryKeys.cart.predicate })
    }
  })
}

export const useCreateCart = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      region_id,
      fields = DEFAULT_CART_FIELDS,
    }: {
      region_id: string;
      fields?: string;
    }) => {
      const metadata = buildCartCreateMetadata()
      const { cart } = await sdk.store.cart.create(
        { region_id, ...(metadata ? { metadata } : {}) },
        { fields }
      )
      setStoredCart(cart.id)
      return cart
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ predicate: queryKeys.cart.predicate })
    },
  })
}

export const useAddToCart = ({ fields }: { fields?: string } = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (variables: {
      variant_id: string;
      quantity: number;
      country_code: string;
      fields?: string;
      product?: HttpTypes.StoreProduct;
      variant?: HttpTypes.StoreProductVariant;
      region?: HttpTypes.StoreRegion;
      metadata?: Record<string, unknown>;
    }) => {
      const { variant_id, quantity, country_code, fields: requestFields, metadata } = variables
      if (!variant_id) throw new Error("Missing variant ID when adding to cart")

      let cartId = getStoredCart()

      if (!cartId) {
        const { regions } = await sdk.store.region.list({})
        const region = regions.find(r =>
          r.countries?.some(c => c.iso_2 === country_code.toLowerCase())
        )
        if (!region) throw new Error(`Region not found for country code: ${country_code}`)
        const cartMetadata = buildCartCreateMetadata()
        const { cart } = await sdk.store.cart.create(
          {
            region_id: region.id,
            ...(cartMetadata ? { metadata: cartMetadata } : {}),
          },
          {
            fields: requestFields || fields || DEFAULT_CART_FIELDS,
          }
        )
        setStoredCart(cart.id)
        cartId = cart.id
      }

      const lineItemPayload: { variant_id: string; quantity: number; metadata?: Record<string, unknown> } = {
        variant_id,
        quantity,
      }
      if (metadata && Object.keys(metadata).length > 0) {
        lineItemPayload.metadata = metadata
      }

      const response = await sdk.store.cart.createLineItem(
        cartId,
        lineItemPayload,
        { fields: requestFields || fields || DEFAULT_CART_FIELDS }
      )
      return response.cart
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ predicate: queryKeys.cart.predicate })
      let previousCart = getCurrentCart(queryClient, fields)
      let didCartExist = true

      if (!previousCart && variables.region) {
        previousCart = createOptimisticCart(variables.region)
        didCartExist = false
      }

      if (previousCart && variables.product && variables.variant) {
        const optimisticItem = createOptimisticCartItem(
          variables.variant,
          variables.product,
          variables.quantity
        )
        addItemOptimistically(queryClient, optimisticItem, previousCart, fields)
      }

      return { didCartExist, previousCart }
    },
    onError: (err, variables, context) => {
      if (context?.previousCart) {
        rollbackOptimisticCart(queryClient, context.previousCart, fields)
      } else if (context && !context.didCartExist) {
        removeStoredCart()
        queryClient.setQueryData(queryKeys.cart.current(fields), null)
      }
    },
    onSettled: (data) => {
      queryClient.invalidateQueries({
        predicate: (query) => queryKeys.cart.predicate(query, fields && data ? [fields] : undefined)
      })
      if (data) {
        queryClient.setQueryData(queryKeys.cart.current(fields), data)
      }
    },
  })
}

export const useUpdateLineItem = ({ fields }: { fields?: string } = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (variables: { line_id: string; quantity: number }) => {
      const cartId = getStoredCart()
      if (!cartId) throw new Error("No cart found")
      const { cart } = await sdk.store.cart.updateLineItem(
        cartId,
        variables.line_id,
        { quantity: variables.quantity },
        { fields: fields || DEFAULT_CART_FIELDS }
      )
      return cart
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        predicate: (query) => queryKeys.cart.predicate(query, fields ? [fields] : undefined)
      })
      const previousCart = getCurrentCart(queryClient, fields)
      if (previousCart) {
        updateLineItemOptimistically(queryClient, variables.line_id, variables.quantity, fields)
      }
      return { previousCart }
    },
    onError: (err, variables, context) => {
      if (context?.previousCart) {
        rollbackOptimisticCart(queryClient, context.previousCart, fields)
      }
    },
    onSettled: (data) => {
      queryClient.invalidateQueries({
        predicate: (query) => queryKeys.cart.predicate(query, fields && data ? [fields] : undefined)
      })
      if (data) {
        queryClient.setQueryData(queryKeys.cart.current(fields), data)
      }
    },
  })
}

export const useDeleteLineItem = ({ fields }: { fields?: string } = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (variables: { line_id: string }) => {
      const cartId = getStoredCart()
      if (!cartId) throw new Error("No cart found")
      await sdk.store.cart.deleteLineItem(cartId, variables.line_id)
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        predicate: (query) => queryKeys.cart.predicate(query, fields ? [fields] : undefined)
      })
      const previousCart = getCurrentCart(queryClient, fields)
      if (previousCart) {
        removeLineItemOptimistically(queryClient, variables.line_id, fields)
      }
      return { previousCart }
    },
    onError: (err, variables, context) => {
      if (context?.previousCart) {
        rollbackOptimisticCart(queryClient, context.previousCart, fields)
      }
    },
    onSettled: (data) => {
      queryClient.invalidateQueries({
        predicate: (query) => queryKeys.cart.predicate(query, fields && data ? [fields] : undefined)
      })
      if (data) {
        queryClient.setQueryData(queryKeys.cart.current(fields), data)
      }
    },
  })
}

export const useApplyPromoCode = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ code }: { code: string }) => {
      const cartId = getStoredCart()
      if (!cartId) throw new Error("No cart found")
      const { cart } = await sdk.client.fetch<{ cart: HttpTypes.StoreCart }>(
        `/store/carts/${cartId}/promotions`,
        {
          method: "POST",
          body: { promo_codes: [code] },
        }
      )
      return cart
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ predicate: queryKeys.cart.predicate })
    },
  })
}

export const useApplyGiftCard = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ code }: { code: string }) => {
      const cartId = getStoredCart()
      if (!cartId) throw new Error("No cart found")
      const { cart } = await sdk.client.fetch<{ cart: HttpTypes.StoreCart }>(
        `/store/carts/${cartId}/gift-cards`,
        {
          method: "POST",
          body: { code },
          query: { fields: DEFAULT_CART_FIELDS },
        }
      )
      return cart
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ predicate: queryKeys.cart.predicate })
    },
  })
}

export const useRemoveGiftCard = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ code }: { code: string }) => {
      const cartId = getStoredCart()
      if (!cartId) throw new Error("No cart found")
      const { cart } = await sdk.client.fetch<{ cart: HttpTypes.StoreCart }>(
        `/store/carts/${cartId}/gift-cards`,
        {
          method: "DELETE",
          body: { code },
          query: { fields: DEFAULT_CART_FIELDS },
        }
      )
      return cart
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ predicate: queryKeys.cart.predicate })
    },
  })
}

export const useRemovePromoCode = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ code }: { code: string }) => {
      const cartId = getStoredCart()
      if (!cartId) throw new Error("No cart found")
      const { cart } = await sdk.client.fetch<{ cart: HttpTypes.StoreCart }>(
        `/store/carts/${cartId}/promotions`,
        {
          method: "DELETE",
          body: { promo_codes: [code] },
        }
      )
      return cart
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ predicate: queryKeys.cart.predicate })
    },
  })
}
